if (!customElements.get('total-price-with-discount')) {
  class TotalPriceWithDiscount extends HTMLElement {
    constructor() {
      super();
      this.variantId = null;
      this.quantity = 1;
      this.productTiers = null;
      this.variantTiers = null;
      this.ignoreVariantsMetafields = false;
      this.currentVariantPrice = null;
      this.currentVariantCompareAtPrice = null;
      this.productCompareAtPrice = null;
      this.variantSelector = null;
      this.productOptions = null;
      this.productCard = null;
      this.variantObserver = null;
      this.boundHandleVariantChange = null;
      this.boundHandleQuantityChange = null;
      this.displayMode = 'both'; // 'both', 'original', or 'discounted'
    }

    connectedCallback() {
      this.initialize();
      this.setupEventListeners();
      this.updatePrice();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    initialize() {
      // Check if we're inside a product-card element
      // CRITICAL: Price components inside product-cards should ONLY use product-card's data-variant-id
      this.productCard = this.closest('product-card');
      
      // Find product-options component to get variant and quantity (only if not in product-card)
      if (!this.productCard) {
        this.productOptions = this.closest('product-options');
      }
      
      // Get initial variant ID
      this.updateCurrentVariantId();
      
      // Get initial quantity
      this.updateCurrentQuantity();

      // Get product tiers from data attribute
      const productTiersData = this.getAttribute('data-product-tiers');
      if (productTiersData) {
        try {
          const parsed = JSON.parse(productTiersData);
          this.productTiers = Array.isArray(parsed.tiers) ? parsed.tiers : [];
          this.ignoreVariantsMetafields = parsed.ignoreVariantsMetafields || false;
        } catch (e) {
          console.error('TotalPriceWithDiscount: Failed to parse product tiers', e);
          this.productTiers = [];
          this.ignoreVariantsMetafields = false;
        }
      }

      // Get variant tiers from data attribute
      const variantTiersData = this.getAttribute('data-variant-tiers');
      if (variantTiersData) {
        try {
          const parsed = JSON.parse(variantTiersData);
          this.variantTiers = Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          console.error('TotalPriceWithDiscount: Failed to parse variant tiers', e);
          this.variantTiers = [];
        }
      }

      // Get display mode (both, original, or discounted)
      this.displayMode = this.getAttribute('data-display-mode') || 'both';

      // Get product-level compare_at_price from data attribute (fallback)
      const comparePriceData = this.getAttribute('data-compare-price');
      if (comparePriceData) {
        const comparePrice = parseInt(comparePriceData, 10);
        if (comparePrice > 0) {
          this.productCompareAtPrice = comparePrice;
        }
      }

      // Set placeholder content
      if (!this.textContent.trim()) {
        const placeholderPrice = this.getAttribute('data-placeholder-price');
        if (placeholderPrice) {
          this.textContent = placeholderPrice;
        }
      }
    }

    updateCurrentVariantId() {
      // CRITICAL: Always prioritize product-card's data-variant-id if inside one
      // Total-price components inside product-cards should ONLY use the product-card's variant ID
      if (this.productCard) {
        const variantIdAttr = this.productCard.getAttribute('data-variant-id');
        if (variantIdAttr) {
          this.variantId = parseInt(variantIdAttr);
          return;
        }
      }
      
      // Not in product-card: try to get from product-options component
      if (this.productOptions) {
        if (this.productOptions.variantSelector && this.productOptions.variantSelector.value) {
          this.variantId = parseInt(this.productOptions.variantSelector.value);
          this.variantSelector = this.productOptions.variantSelector;
          return;
        }
      }
      
      // Fallback: try to get from main product form
      const mainForm = document.getElementById('main-product-form');
      if (mainForm) {
        const variantSelector = mainForm.querySelector('select[name="id"]');
        if (variantSelector && variantSelector.value) {
          this.variantId = parseInt(variantSelector.value);
          this.variantSelector = variantSelector;
          return;
        }
      }
    }

    updateCurrentQuantity() {
      // Try to get from product-options component
      if (this.productOptions) {
        const selectedQuantity = this.productOptions.getSelectedQuantity();
        if (selectedQuantity) {
          this.quantity = selectedQuantity;
          return;
        }
      }
      
      // Fallback: try to get from quantity input
      const quantityInput = document.querySelector('input[name="quantity"]:checked');
      if (quantityInput) {
        this.quantity = parseInt(quantityInput.value) || 1;
      } else {
        this.quantity = 1;
      }
    }

    setupEventListeners() {
      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      this.boundHandleQuantityChange = this.handleQuantityChange.bind(this);
      
      if (this.productCard) {
        // Setup listeners for product-card context
        // CRITICAL: Total-price components inside product-cards should ONLY use the product-card's data-variant-id
        // and should NOT listen to product-options:changed events to prevent cross-contamination
        
        // Watch for changes to data-variant-id on the product-card
        this.variantObserver = new MutationObserver(() => {
          const oldVariantId = this.variantId;
          this.updateCurrentVariantId();
          if (oldVariantId !== this.variantId) {
            this.updatePrice();
          }
        });

        // Observe the product-card for data-variant-id changes
        this.variantObserver.observe(this.productCard, {
          attributes: true,
          attributeFilter: ['data-variant-id']
        });

        // DO NOT listen to product-options:changed events when inside product-card
        // The MutationObserver above handles all variant changes via product-card's data-variant-id
        
        // Listen for quantity changes (still needed for quantity picker)
        document.addEventListener('change', (e) => {
          // Only process quantity changes within this product-card
          if (e.target.name === 'quantity' && e.target.type === 'radio' && this.productCard.contains(e.target)) {
            this.boundHandleQuantityChange(e);
          }
        });
      } else {
        // Setup listeners for product form context (not in product-card)
        // Listen for variant changes
        if (this.variantSelector) {
          this.variantSelector.addEventListener('change', this.boundHandleVariantChange);
        }
        
        // Listen for custom variant change events, but filter by product context
        document.addEventListener('product-options:changed', this.boundHandleVariantChange);
        
        // Listen for quantity changes
        document.addEventListener('change', (e) => {
          if (e.target.name === 'quantity' && e.target.type === 'radio') {
            this.boundHandleQuantityChange(e);
          }
        });
      }
    }

    handleVariantChange(event) {
      // CRITICAL: If inside product-card, ignore product-options:changed events
      // Total-price components inside product-cards should ONLY use product-card's data-variant-id
      if (this.productCard) {
        return; // Ignore event, rely on MutationObserver for product-card changes
      }
      
      // For non-product-card contexts, filter events by product context
      if (event && event.detail) {
        const { productHandle: eventProductHandle, sourceFormId: eventFormId, sourceId: eventSourceId } = event.detail;
        
        // Check if event is from a different product/form
        if (eventProductHandle && this.productOptions && this.productOptions.productHandle) {
          if (eventProductHandle !== this.productOptions.productHandle) {
            return; // Ignore events from different products
          }
        }
        
        // Check if event is from a different product-options instance
        if (eventSourceId && this.productOptions && this.productOptions.id && eventSourceId !== this.productOptions.id) {
          return; // Ignore events from different product-options instances
        }
        
        // Check form ID if available
        if (eventFormId && this.productOptions) {
          const form = this.productOptions.closest('form[action*="/cart/add"]');
          const thisFormId = form ? form.id : (this.productOptions.getAttribute('data-form-id') || null);
          if (thisFormId && eventFormId !== thisFormId) {
            return; // Ignore events from different forms
          }
        }
      }
      
      const oldVariantId = this.variantId;
      this.updateCurrentVariantId();
      
      if (oldVariantId !== this.variantId) {
        this.updatePrice();
      }
    }

    handleQuantityChange(event) {
      const newQuantity = parseInt(event.target.value) || 1;
      if (newQuantity !== this.quantity) {
        this.quantity = newQuantity;
        this.updatePrice();
      }
    }

    getDiscountTiersForVariant(variantId) {
      // Normalize variant ID to number for comparison
      const normalizedVariantId = typeof variantId === 'string' ? parseInt(variantId, 10) : variantId;
      
      // If variant tiers exist and ignoreVariantsMetafields is false, use variant tiers
      if (this.variantTiers && Array.isArray(this.variantTiers) && !this.ignoreVariantsMetafields) {
        // Compare both as numbers to handle type mismatches
        const variantData = this.variantTiers.find(v => {
          const vId = typeof v.id === 'string' ? parseInt(v.id, 10) : v.id;
          return vId === normalizedVariantId;
        });
        // If variant has tiers property, use it (even if empty array) - variant takes priority
        if (variantData && variantData.tiers !== undefined) {
          // Handle nested tiers structure: { tiers: { tiers: [...] } }
          let tiersArray = null;
          if (Array.isArray(variantData.tiers)) {
            tiersArray = variantData.tiers;
          } else if (variantData.tiers && variantData.tiers.tiers && Array.isArray(variantData.tiers.tiers)) {
            tiersArray = variantData.tiers.tiers;
          }
          // Return tiers array (even if empty) - don't fall back to product
          return tiersArray || [];
        }
      }
      
      // Only fall back to product-level tiers if variant has no tiers property at all
      if (this.productTiers && Array.isArray(this.productTiers) && this.productTiers.length > 0) {
        return this.productTiers;
      }
      
      return [];
    }

    getVariantPrice(variantId) {
      // Normalize variant ID to number for comparison
      const normalizedVariantId = typeof variantId === 'string' ? parseInt(variantId, 10) : variantId;
      
      if (this.variantTiers && Array.isArray(this.variantTiers)) {
        // Compare both as numbers to handle type mismatches
        const variantData = this.variantTiers.find(v => {
          const vId = typeof v.id === 'string' ? parseInt(v.id, 10) : v.id;
          return vId === normalizedVariantId;
        });
        if (variantData && variantData.price) {
          // Price is already in cents from Liquid
          return parseInt(variantData.price, 10);
        }
      }
      
      // Fallback: try to get from quantity container data attribute
      const quantityContainer = document.querySelector('[data-quantity-options-container="true"]');
      if (quantityContainer && quantityContainer.dataset.variantPrice) {
        return parseInt(quantityContainer.dataset.variantPrice);
      }
      
      // Fallback: parse from placeholder
      const placeholderPrice = this.getAttribute('data-placeholder-price');
      if (placeholderPrice) {
        // Remove currency symbols and parse, then convert to cents
        const numericPrice = parseFloat(placeholderPrice.replace(/[^0-9.]/g, ''));
        return window.theme?.utils?.roundPrice ? 
          window.theme.utils.roundPrice(numericPrice * 100, true) : 
          Math.round(numericPrice * 100); // Convert to cents
      }
      
      return null;
    }

    getVariantCompareAtPrice(variantId) {
      // Normalize variant ID to number for comparison
      const normalizedVariantId = typeof variantId === 'string' ? parseInt(variantId, 10) : variantId;
      
      if (this.variantTiers && Array.isArray(this.variantTiers)) {
        // Compare both as numbers to handle type mismatches
        const variantData = this.variantTiers.find(v => {
          const vId = typeof v.id === 'string' ? parseInt(v.id, 10) : v.id;
          return vId === normalizedVariantId;
        });
        if (variantData && variantData.compare_at_price) {
          // compare_at_price is already in cents from Liquid
          const comparePrice = parseInt(variantData.compare_at_price, 10);
          if (comparePrice > 0) {
            return comparePrice;
          }
        }
      }
      
      // Fallback to product-level compare_at_price
      if (this.productCompareAtPrice && this.productCompareAtPrice > 0) {
        return this.productCompareAtPrice;
      }
      
      return null;
    }

    calculateDiscountedPrice(basePrice, discountPercentage) {
      if (!basePrice || !discountPercentage) {
        return basePrice;
      }

      const discountDecimal = discountPercentage / 100.0;
      const discountAmount = basePrice * discountDecimal;
      const discountedPrice = basePrice - discountAmount;
      
      return window.theme?.utils?.roundPrice ? 
        window.theme.utils.roundPrice(discountedPrice, true) : 
        Math.round(discountedPrice); // Fallback to nearest cent
    }

    formatMoney(cents) {
      // Use existing utility function if available
      if (typeof window.theme?.utils?.money === 'function') {
        const amount = cents / 100;
        return window.theme.utils.money(amount);
      }
      
      // Fallback: format manually using Shopify currency
      const currency = window.Shopify?.currency || { active: 'GBP', rate: '1.0' };
      const currencyCode = currency.active || 'GBP';
      const baseAmount = cents / 100;
      
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(baseAmount);
    }

    updatePrice() {
      if (!this.variantId) {
        this.updateCurrentVariantId();
        if (!this.variantId) {
          return;
        }
      }

      // Update quantity
      this.updateCurrentQuantity();

      const variantPrice = this.getVariantPrice(this.variantId);
      if (!variantPrice) {
        return;
      }

      this.currentVariantPrice = variantPrice;
      
      // Get compare_at_price for this variant (used as original price when available)
      const variantCompareAtPrice = this.getVariantCompareAtPrice(this.variantId);
      this.currentVariantCompareAtPrice = variantCompareAtPrice;
      
      const tiers = this.getDiscountTiersForVariant(this.variantId);
      
      // Find the tier that matches the selected quantity
      let matchingTier = null;
      if (tiers && tiers.length > 0) {
        // Find tier with matching quantity
        matchingTier = tiers.find(tier => parseInt(tier.quantity || '1') === this.quantity);
        // If no exact match, use the first tier (default)
        if (!matchingTier) {
          matchingTier = tiers[0];
        }
      }

      // Calculate total prices (variant price * quantity)
      const basePrice = variantPrice * this.quantity;
      
      // Determine the "original price" to display - use compare_at_price if available and higher
      const totalCompareAtPrice = variantCompareAtPrice ? variantCompareAtPrice * this.quantity : null;
      const originalPriceCents = totalCompareAtPrice && totalCompareAtPrice > basePrice 
        ? totalCompareAtPrice 
        : basePrice;
      
      const discountPercentage = matchingTier ? (parseFloat(matchingTier.discount_amount_value) || 0) : 0;
      
      // If no discount tiers but compare_at_price exists, we still show the compare_at_price as original
      // The discounted price is the base price (variant price * quantity)
      const discountedPriceCents = discountPercentage > 0 
        ? this.calculateDiscountedPrice(basePrice, discountPercentage)
        : basePrice;
      
      const originalPriceFormatted = this.formatMoney(originalPriceCents);
      const discountedPriceFormatted = this.formatMoney(discountedPriceCents);

      // Check if prices are the same (no actual discount applied)
      const pricesAreSame = originalPriceCents === discountedPriceCents;

      // Build HTML based on display mode
      const discountedPriceHtml = `<span>${discountedPriceFormatted}</span>`;
      const originalPriceHtml = pricesAreSame ? '' : `<span>${originalPriceFormatted}</span>`;
      
      let content = '';
      if (this.displayMode === 'original') {
        content = originalPriceHtml;
      } else if (this.displayMode === 'discounted') {
        content = discountedPriceHtml;
      } else {
        // Show both prices (default: 'both')
        // When compare_at_price exists, show discounted price first, then original (crossed out)
        content = discountedPriceHtml + originalPriceHtml;
      }
      
      this.innerHTML = content;
    }

    cleanup() {
      // Disconnect the variant observer if it exists
      if (this.variantObserver) {
        this.variantObserver.disconnect();
        this.variantObserver = null;
      }
      
      // Remove event listeners (only if they were added - not added for product-card context)
      if (this.boundHandleVariantChange && !this.productCard) {
        document.removeEventListener('product-options:changed', this.boundHandleVariantChange);
      }
      
      if (this.variantSelector && this.boundHandleVariantChange) {
        this.variantSelector.removeEventListener('change', this.boundHandleVariantChange);
      }
    }
  }

  customElements.define('total-price-with-discount', TotalPriceWithDiscount);
}

